import React, {
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
} from "react";
import { logError } from "core/utils/logger";
import { useNavigate } from "react-router";
import { loginService as aFLoginService } from "core/services/ac2Flywheel/authService";
import { getProvidersFromBackend } from "modules/chat/services/chatService";
import { useAppDispatch } from "core/store/reduxStore";
import {
  fetchTokenService as AuthTokenService,
  userService,
  logoutService,
  checkTokenService,
} from "core/services/agentMySecurity/authService";

import { loginService as amsLoginService } from "core/services/agentMySecurity/authService";
import { Toast as showToast } from "core/components/global/toast/toast";
import { setUserService } from "core/services/ac2Flywheel/userService";
import { SessionExpiredOverlay } from "core/components/global/SessionExpiredOverlay/SessionExpiredOverlay";
import { setProviders } from "modules/chat/store/conversationList";
import apiClient from "modules/pentest/services/api"
import ctemApiClient from "modules/ctem/services/api"


export type UserContextType = {
  afUser: UserClaims | null;
  setAFUser: Dispatch<SetStateAction<UserClaims | null>>;
  amsUser: UserClaims | null;
  setAMSUser: Dispatch<SetStateAction<UserClaims | null>>;
  saveUser: any;
  userAdGroups: string[];
  setUserAdGroups: Dispatch<SetStateAction<string[]>>;
  amsBearerToken: string | null;
  setAMSBearerToken: Dispatch<SetStateAction<string>>;
  aFAccessToken: string | null;
  setAFAccessToken: Dispatch<SetStateAction<string>>;
  aFRefreshToken: string | null;
  setAFRefreshToken: Dispatch<SetStateAction<string | null>>;
  loginAF: () => void;
  loginAMS: () => void;
  signOut: () => void;
  isAvailableAuthUser: boolean;
  setIsAvailableAuthUser: Dispatch<SetStateAction<boolean>>;
  setAuthToken: any;
  getAMSUser: any;
  checkAuthToken: any;
  initializeAuth: () => Promise<void>;
  isAuthInitialized: boolean;
};

const initialState: UserContextType = {
  afUser: null,
  setAFUser: () => undefined,
  amsUser: null,
  setAMSUser: () => undefined,
  userAdGroups: [],
  setUserAdGroups: () => undefined,
  amsBearerToken: null,
  setAMSBearerToken: () => undefined,
  aFAccessToken: null,
  setAFAccessToken: () => undefined,
  aFRefreshToken: null,
  setAFRefreshToken: () => undefined,
  saveUser: () => undefined,
  loginAF: () => undefined,
  loginAMS: () => undefined,
  signOut: () => undefined,
  isAvailableAuthUser: false,
  setIsAvailableAuthUser: () => undefined,
  setAuthToken: () => undefined,
  checkAuthToken: () => undefined,
  getAMSUser: () => undefined,
  initializeAuth: () => Promise.resolve(),
  isAuthInitialized: false,
};

export interface UserClaims {
  sub: string;
  given_name: string;
  family_name: string;
  email_verified: boolean;
  preferred_username: string;
  auth_time?: Date;
  aud?: string;
  lastLoggedIn?: Date;
  image?: string;
  firstLetter?: string;
  email: string;
  groups?: string[];
  username?: string;
}

export const AuthContext = createContext<UserContextType>(initialState);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [afUser, setAFUser] = useState<UserClaims | null>(null);
  const [amsUser, setAMSUser] = useState<any>(null);
  const [amsBearerToken, setAMSBearerToken] = useState<string>("");
  const [aFAccessToken, setAFAccessToken] = useState<string>("");
  const [aFRefreshToken, setAFRefreshToken] = useState<string | null>(null);
  const [isAvailableAuthUser, setIsAvailableAuthUser] =
    useState<boolean>(false);
  const [userAdGroups, setUserAdGroups] = useState<string[]>([]);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  
  // Track token refresh operations
  const isRefreshing = useRef<boolean>(false);
  const lastRefreshTime = useRef<number>(0);

  const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour to match cookie expiration

  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionTimer = useRef<NodeJS.Timeout>();

  const getAMSUser = async () => {
    console.log(`getAMSUser: `)
    try {
      // Extract the data from the Axios response
      const resp = await userService();

      // Set the user so we can soft prevent them in the UI, invalid authentication
      // will be blocked by the backend anyway
      console.log(`AMS user fetched: `, resp);  
      setAMSUser(resp);

      return resp;
    } catch (error) {
      logError("Error fetching AMS user", error);
      return null;
    }
  };

  const setAuthToken = async () => {
    console.log("Fetching new auth token");
    try {
      const response = await AuthTokenService();
      const data = response.data;
      
      if (!data || !data.token) {
        console.error("No token received from auth service");
        return null;
      }

      // Clean and normalize the token
      let token = data.token.trim();
      
      // Remove any existing bearer prefix to avoid duplication
      token = token.replace(/^bearer\s+/i, "");
      
      // Add bearer prefix consistently
      const normalizedToken = `bearer ${token}`;
      console.log("Token normalized with bearer prefix");

      // Set the token into the Auth context
      setAMSBearerToken(normalizedToken);
      return normalizedToken;
    } catch (error) {
      logError("Error fetching auth token", error);
      return null;
    }
  };

  const checkAuthToken = async () => {
    // Prevent concurrent token checks/refreshes
    console.log(`checkAuthToken, current token: `, amsBearerToken);

    // If we already have a token, validate it
    if (amsBearerToken && amsBearerToken !== "") {
      // Ensure token has proper bearer prefix
      let normalizedToken = amsBearerToken;
      if (!normalizedToken.toLowerCase().startsWith('bearer ')) {
        normalizedToken = `bearer ${normalizedToken}`;
        setAMSBearerToken(normalizedToken);
      }

      // Quick local check to see if token is still valid based on expiry
      try {
        const tokenPart = normalizedToken.split(/\s+/)[1];
        if (tokenPart && tokenPart.split('.').length === 3) {
          const base64Url = tokenPart.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(window.atob(base64));
          
          if (payload.exp && payload.exp > (Date.now() / 1000) + 30) {
            console.log('Using existing valid token');
            return normalizedToken;
          }
        }
      } catch (e) {
        console.log('Error checking token expiry, will validate with server');
      }
    }
    
    // Prevent concurrent refresh operations
    if (isRefreshing.current) {
      console.log('Token refresh already in progress, waiting...');
      await new Promise(resolve => setTimeout(resolve, 750));
      return amsBearerToken;
    }
    
    try {
      isRefreshing.current = true;
      
      // If no token exists, get a new one
      if (!amsBearerToken || amsBearerToken === "") {
        console.log('No token exists, fetching new token');
        const newToken = await setAuthToken();
        return newToken;
      }

      // Validate existing token with backend
      try {
        const tokenPart = amsBearerToken.split(/\s+/)[1];
        const response = await checkTokenService(tokenPart);
        
        if (response && response.status < 300) {
          console.log('Existing token validated successfully');
          return amsBearerToken;
        }
      } catch (error) {
        console.log('Token validation failed, will attempt to get new token');
      }

      // If we get here, either token validation failed or token is expired
      console.log('Fetching new token');
      const newToken = await setAuthToken();
      
      if (!newToken) {
        console.error('Failed to get new token');
        return null;
      }
      
      return newToken;
    } finally {
      isRefreshing.current = false;
    }
  };
  
  apiClient.constructor["setTokenProvider"](checkAuthToken);
  ctemApiClient.constructor["setTokenProvider"](checkAuthToken);

  const saveUser = async ({ givenUser }) => {
    setAFUser(givenUser);
  };

  const loginAF = async () => {
    try {
      localStorage.removeItem('accessDenied');
      aFLoginService();
    } catch (error) {
      navigate("/login/ac2flywheel");
    }
  };

  const loginAMS = async () => {
    try {
      localStorage.removeItem('accessDenied');
      // TODO redirect to our backend, once available
      amsLoginService();

      // TODO delete the following, once the backend is available
      // const token = await checkAuthToken();
      // if (token) {
      //   navigate("/console")
      // }
    } catch (error) {
      console.log(`check token 5`);
      navigate("/console/login");
    }
  };

  const signOut = async () => {
    // Clear user session on the client
    setAMSUser(null);
    setAMSBearerToken("");
    setAFUser(null);
    setAFAccessToken("");
    setAFRefreshToken(null);
    localStorage.setItem('accessDenied', 'true');
    logoutService();
    // Redirect the user to the IDP
    // TODO logoutService() as a redirect once we have an env specific backend; & remove the below
    // const response = await logoutService();
    // if (response === true) {
    //   navigate("/console/login");
    // } else {
    //   navigate("/console/login");
    // }
  };

  const handleRelogin = async () => {
    try {
      setSessionExpired(false);
      navigate("/console/login");
    } catch (error) {
      logError("Relogin failed", error);
      navigate("/console/login");
    }
  };

  useEffect(() => {
    if (amsUser && amsBearerToken !== "") {
      /*
          After a successfull Ac2/SOC AI login, make sure that the user and session is reflected in the
          backend db. This will assist with managing features such as user comments or user
          profiles.
        */
      try {
        // Extract first name and last name from username if available
        let firstName = "";
        let lastName = "";

        if (amsUser.username && amsUser.username.includes(",")) {
          // Format is usually "Last, First" - split and trim
          const nameParts = amsUser.username.split(",");
          lastName = nameParts[0].trim();
          firstName = nameParts.length > 1 ? nameParts[1].trim() : "";
        }

        // Add user to AC2 database
        setUserService(
          { Authorization: amsBearerToken, "Content-Type": "application/json" },
          amsUser.email,
          firstName,
          lastName,
          Date.now(),
          "ac2"
        ).catch((error) => {
          logError("Error updating AC2 user service", error);
        });

        // Add user to Flywheel database
        setUserService(
          { Authorization: amsBearerToken, "Content-Type": "application/json" },
          amsUser.email,
          firstName,
          lastName,
          Date.now(),
          "socai"
        ).catch((error) => {
          logError("Error updating user service", error);
        });
      } catch (error) {
        logError("Error in setUserService", error);
      }
    }
  }, [amsUser, amsBearerToken])

  // This is commented out to avoid conflicts with the SessionTimeout component
  // which already manages the session timeout using constants.ts settings
  /*
    useEffect(() => {
      const resetSessionTimer = () => {
        if (sessionTimer.current) {
          clearTimeout(sessionTimer.current);
        }
        sessionTimer.current = setTimeout(() => {
          setSessionExpired(true);
          // When session expires, clear tokens to prevent further API calls
          setAMSBearerToken("");
          setAFAccessToken("");
        }, SESSION_TIMEOUT);
      };

      // Reset timer on user activity
      const handleUserActivity = () => {
        if (!sessionExpired) {
          resetSessionTimer();
        }
      };

      window.addEventListener('mousedown', handleUserActivity);
      window.addEventListener('keydown', handleUserActivity);
      window.addEventListener('scroll', handleUserActivity);

      resetSessionTimer();

      return () => {
        if (sessionTimer.current) {
          clearTimeout(sessionTimer.current);
        }
        window.removeEventListener('mousedown', handleUserActivity);
        window.removeEventListener('keydown', handleUserActivity);
        window.removeEventListener('scroll', handleUserActivity);
      };
    }, [sessionExpired]);
    */

  const initializeOther = async (token) => {
      // Get user info
      console.log('Fetching AMS user info with token');
      const amsUserInfo = await getAMSUser();
      console.log('AMS user info:', amsUserInfo); 

      if (amsUserInfo) {
        console.log('Valid AMS user found, setting auth state');  
        setIsAvailableAuthUser(true);
        
        // Get provider names if needed
        try {
          // Get provider names for the dropdown
          console.log('Fetching provider names with token');
          const names = await getProvidersFromBackend(token || amsBearerToken);
          
          console.log("Provider names fetched:", names);

          if (names) {
            dispatch(setProviders({ providers: names }));
          }
        } catch (error) {
          console.error('Error fetching providers:', error);
        }
      } else {
        console.log('No valid AMS user, redirecting to login');
        navigate('/console/login');
        return;
      }
      
      setIsAuthInitialized(true);
      console.log('Auth initialization complete');
  }

  // Centralized auth initialization method
  const initializeAuth = async () => {
    console.log('Initializing authentication...');
    try {
      // First check for AMS token
      const token = await checkAuthToken();
      
      console.log(`initializeAuth received token: ${token ? 'yes' : 'no'}, value: ${token}`);
      
      if (!token) {
        // We don't have a token - try up to 3 times with a delay
        // This helps with SAML redirects where token might not be immediately available
        for (let attempt = 0; attempt < 3; attempt++) {
          console.log(`No token found, retry attempt ${attempt + 1}/3. Current token state:`, amsBearerToken);
          await new Promise(resolve => setTimeout(resolve, 750));
          
          // Check if token appeared in state while we were waiting
          if (amsBearerToken && amsBearerToken !== "") {
            console.log(`Token appeared in state during wait: ${amsBearerToken}`);
            await initializeOther(amsBearerToken);
            return;
          }
          
          const retryToken = await checkAuthToken();
          console.log(`Retry ${attempt + 1} returned token: ${retryToken ? 'yes' : 'no'}`);
          
          if (retryToken) {
            console.log('Token obtained on retry:', retryToken);
            await initializeOther(retryToken);
            return;
          }
        }
        
        // If we get here, we failed to get a token after retries
        console.log('Failed to get token after retries, redirecting to login. Final token state:', amsBearerToken);
        navigate('/console/login');
        return;
      } else {
        console.log('Valid token found on first attempt:', token);
        await initializeOther(token);
      }
    } catch (error) {
      console.error('Error during auth initialization:', error);
      navigate('/console/login');
    }
  };
  
  return (
    <AuthContext.Provider
      value={{
        saveUser,
        afUser,
        setAFUser,
        amsUser,
        setAMSUser,
        userAdGroups,
        setUserAdGroups,
        amsBearerToken,
        setAMSBearerToken,
        aFAccessToken,
        setAFAccessToken,
        aFRefreshToken,
        setAFRefreshToken,
        loginAF,
        signOut,
        loginAMS,
        isAvailableAuthUser,
        setIsAvailableAuthUser,
        setAuthToken,
        checkAuthToken,
        getAMSUser,
        initializeAuth,
        isAuthInitialized,
      }}
    >
      {children}
      {sessionExpired && <SessionExpiredOverlay onRelogin={handleRelogin} />}
    </AuthContext.Provider>
  );
};
